// MediaRecorder writes a `Duration` placeholder (float64 = 0) inside WebM `Segment > Info`
// but never fills it in, which makes `video.duration === Infinity` in the browser and
// breaks `currentTime` assignments once the video has played through once.
// This helper overwrites the 8-byte placeholder in place with the real duration.
// Crucially, it never alters the byte layout of the file: any container size VINTs
// stay untouched so that subsequent Tracks/Clusters stay at the exact same byte offsets
// (otherwise the browser's internal seek index drifts and seeks break).
// If the Duration slot isn't present or isn't 8 bytes, the helper bails and returns the
// original blob — we prefer a missing duration over a shifted byte layout.

const EBML_ID_SEGMENT = [0x18, 0x53, 0x80, 0x67];
const EBML_ID_INFO = [0x15, 0x49, 0xa9, 0x66];
const EBML_ID_TIMECODE_SCALE = [0x2a, 0xd7, 0xb1];
const EBML_ID_DURATION = [0x44, 0x89];
const DEFAULT_TIMECODE_SCALE_NS = 1_000_000;

interface Vint {
  value: number;
  length: number;
  unknown: boolean;
}

function readVint(data: Uint8Array, pos: number): Vint {
  const first = data[pos];
  if (first === undefined || first === 0) throw new Error(`Invalid VINT at ${pos}`);
  let length = 1;
  let mask = 0x80;
  while (!(first & mask)) {
    length++;
    mask >>= 1;
  }
  if (length > 8) throw new Error(`VINT too long at ${pos}`);
  let value = first & (mask - 1);
  let unknown = value === mask - 1;
  for (let i = 1; i < length; i++) {
    const b = data[pos + i];
    if (b === undefined) throw new Error(`VINT truncated at ${pos}`);
    unknown = unknown && b === 0xff;
    value = value * 256 + b;
  }
  return { value, length, unknown };
}

function bytesEqual(a: Uint8Array, b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function encodeFloat64BE(value: number): Uint8Array {
  const buf = new ArrayBuffer(8);
  new DataView(buf).setFloat64(0, value, false);
  return new Uint8Array(buf);
}

function readUint(data: Uint8Array, start: number, end: number): number {
  let v = 0;
  for (let i = start; i < end; i++) v = v * 256 + data[i]!;
  return v;
}

interface Element {
  start: number;
  end: number;
  contentStart: number;
  contentEnd: number;
  sizeKnown: boolean;
  idBytes: Uint8Array;
}

function readElement(data: Uint8Array, pos: number, parentEnd: number): Element {
  const idVint = readVint(data, pos);
  const idBytes = data.subarray(pos, pos + idVint.length);
  const size = readVint(data, pos + idVint.length);
  const contentStart = pos + idVint.length + size.length;
  const contentEnd = size.unknown ? parentEnd : contentStart + size.value;
  return {
    start: pos,
    end: contentEnd,
    contentStart,
    contentEnd,
    sizeKnown: !size.unknown,
    idBytes,
  };
}

function findChild(
  data: Uint8Array,
  start: number,
  end: number,
  targetId: readonly number[],
): Element | null {
  let pos = start;
  while (pos < end) {
    const el = readElement(data, pos, end);
    if (bytesEqual(el.idBytes, targetId)) return el;
    if (!el.sizeKnown) return null;
    pos = el.contentEnd;
  }
  return null;
}

function patchWebmDuration(data: Uint8Array, durationMs: number): Uint8Array | null {
  const segment = findChild(data, 0, data.length, EBML_ID_SEGMENT);
  if (!segment) return null;

  const info = findChild(data, segment.contentStart, segment.contentEnd, EBML_ID_INFO);
  if (!info || !info.sizeKnown) return null;

  const duration = findChild(data, info.contentStart, info.contentEnd, EBML_ID_DURATION);
  if (!duration || duration.contentEnd - duration.contentStart !== 8) return null;

  const tsEl = findChild(data, info.contentStart, info.contentEnd, EBML_ID_TIMECODE_SCALE);
  const timecodeScale = tsEl
    ? readUint(data, tsEl.contentStart, tsEl.contentEnd) || DEFAULT_TIMECODE_SCALE_NS
    : DEFAULT_TIMECODE_SCALE_NS;

  const durationValue = (durationMs * 1_000_000) / timecodeScale;
  const out = new Uint8Array(data);
  out.set(encodeFloat64BE(durationValue), duration.contentStart);
  return out;
}

export async function fixWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  if (!durationMs || durationMs <= 0) return blob;
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const patched = patchWebmDuration(bytes, durationMs);
    return patched ? new Blob([patched as BlobPart], { type: blob.type }) : blob;
  } catch (err) {
    console.warn('[fix-webm-duration] failed; returning original blob', err);
    return blob;
  }
}
